import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { useCallback, useContext, useEffect, useState } from 'react';
import { TableHeader } from '../../../components/Containers/ContainerTable';
import SnackBarContext from '../../../context/snackbar';
import UserContext from '../../../context/user';
import useShippingLineConfigApiRoutes from '../../../hooks/api/useShippingLineConfigApiRoutes';
import { ShippingLineConfig, ShippingLineEndpointType } from '../../../../types';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    wrap: {
      backgroundColor: 'white',
      borderRadius: 24,
      padding: theme.spacing(2),
      marginTop: theme.spacing(3),
      boxShadow: '0px 2px 16px 1px rgba(0, 0, 0, 0.12)',
    },
  })
);

type EditState = {
  shippingLineCode: string;
  endpointType: ShippingLineEndpointType;
  email: string;
  bccEmail: string;
  webhookUrl: string;
  enabled: boolean;
  aiDecisions: boolean;
};

const EndpointConfigTable = () => {
  const classes = useStyles();
  const {
    isLoggedIn,
    constants: { shippingLines },
  } = useContext(UserContext);
  const { showSnack } = useContext(SnackBarContext);
  const { getShippingLineConfigs, upsertShippingLineConfig } = useShippingLineConfigApiRoutes();

  const [configs, setConfigs] = useState<ShippingLineConfig[]>([]);
  const [defaultBcc, setDefaultBcc] = useState('');
  const [edit, setEdit] = useState<EditState | undefined>();
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    getShippingLineConfigs().then((response) => {
      setConfigs(response.shippingLineConfigs);
      setDefaultBcc(response.defaultBcc);
    });
  }, [getShippingLineConfigs]);

  useEffect(() => {
    if (isLoggedIn) load();
  }, [isLoggedIn, load]);

  const configFor = (code: string) => configs.find((c) => c.shippingLineCode === code);

  const openEdit = (code: string) => {
    const existing = configFor(code);
    setEdit({
      shippingLineCode: code,
      endpointType: existing?.endpointType || 'email',
      email: existing?.email || '',
      // New configs prefill the suggested default BCC; existing configs show
      // exactly what is stored (empty = requests send with no BCC).
      bccEmail: existing ? existing.bccEmail || '' : defaultBcc,
      webhookUrl: existing?.webhookUrl || '',
      enabled: existing?.enabled ?? true,
      aiDecisions: existing?.aiDecisions ?? false,
    });
  };

  const submit = () => {
    if (!edit) return;
    setSubmitting(true);
    upsertShippingLineConfig(edit.shippingLineCode, {
      endpointType: edit.endpointType,
      email: edit.email,
      bccEmail: edit.bccEmail,
      webhookUrl: edit.webhookUrl,
      enabled: edit.enabled,
      aiDecisions: edit.aiDecisions,
    })
      .then(() => {
        showSnack('Endpoint saved.', 'success');
        setEdit(undefined);
        load();
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <Box className={classes.wrap}>
      <Typography variant="h6" mb={1}>
        Reuse Request Endpoints
      </Typography>
      <Typography variant="body2" color="textSecondary" mb={2}>
        When a container of a configured shipping line is reserved, the request is sent to its endpoint and held as
        pending until approved. Lines without an enabled endpoint keep the instant-assign behaviour.
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableHeader label="SHIPPING LINE" />
              <TableHeader label="TYPE" />
              <TableHeader label="DESTINATION" />
              <TableHeader label="STATUS" />
              <TableHeader label="AI DECISIONS" />
              <TableHeader sx={{ minWidth: 120 }} label="" />
            </TableRow>
          </TableHead>
          <TableBody>
            {shippingLines.map((line) => {
              const config = configFor(line.code);
              const destination = config
                ? config.endpointType === 'email'
                  ? config.email
                  : config.webhookUrl
                : '—';
              return (
                <TableRow key={line.code}>
                  <TableCell>{line.name}</TableCell>
                  <TableCell>{config ? config.endpointType : '—'}</TableCell>
                  <TableCell>
                    {destination || '—'}
                    {config?.endpointType === 'email' && config?.bccEmail ? (
                      <Typography variant="caption" display="block" color="text.secondary">
                        bcc: {config.bccEmail}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    {!config ? (
                      <Chip size="small" label="Not configured" />
                    ) : config.enabled ? (
                      <Chip size="small" color="success" label="Enabled" />
                    ) : (
                      <Chip size="small" label="Disabled" />
                    )}
                  </TableCell>
                  <TableCell>
                    {config?.aiDecisions ? (
                      <Chip
                        size="small"
                        label="Auto"
                        sx={{ fontWeight: 600, color: '#0f7ab5', background: 'rgba(0,136,207,0.12)' }}
                      />
                    ) : (
                      <Chip size="small" label="Review only" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" color="info" variant="outlined" onClick={() => openEdit(line.code)}>
                      Configure
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!edit} onClose={() => setEdit(undefined)} fullWidth maxWidth="sm">
        <DialogTitle>
          Configure endpoint {edit ? `- ${shippingLines.find((l) => l.code === edit.shippingLineCode)?.name}` : ''}
        </DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            margin="normal"
            label="Endpoint type"
            value={edit?.endpointType || 'email'}
            onChange={(e) =>
              setEdit((prev) => (prev ? { ...prev, endpointType: e.target.value as ShippingLineEndpointType } : prev))
            }
          >
            <MenuItem value="email">Email</MenuItem>
            <MenuItem value="webhook">Webhook</MenuItem>
          </TextField>
          {edit?.endpointType === 'webhook' ? (
            <TextField
              fullWidth
              margin="normal"
              label="Webhook URL"
              value={edit?.webhookUrl || ''}
              onChange={(e) => setEdit((prev) => (prev ? { ...prev, webhookUrl: e.target.value } : prev))}
            />
          ) : (
            <>
              <TextField
                fullWidth
                margin="normal"
                label="Email address"
                value={edit?.email || ''}
                onChange={(e) => setEdit((prev) => (prev ? { ...prev, email: e.target.value } : prev))}
              />
              <TextField
                fullWidth
                margin="normal"
                label="BCC address (optional)"
                helperText="Copied on every reuse request sent to this line. Remove it to send with no BCC."
                value={edit?.bccEmail || ''}
                onChange={(e) => setEdit((prev) => (prev ? { ...prev, bccEmail: e.target.value } : prev))}
              />
            </>
          )}
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                checked={edit?.enabled ?? true}
                onChange={(e) => setEdit((prev) => (prev ? { ...prev, enabled: e.target.checked } : prev))}
              />
            }
            label="Enabled"
          />
          <FormControlLabel
            sx={{ mt: 1, display: 'flex' }}
            control={
              <Switch
                checked={edit?.aiDecisions ?? false}
                onChange={(e) => setEdit((prev) => (prev ? { ...prev, aiDecisions: e.target.checked } : prev))}
              />
            }
            label={
              <Box>
                AI decisions
                <Typography variant="caption" display="block" color="text.secondary">
                  On: this line's request emails reply to the StackIQ inbox, and StackIQ reads the replies and
                  auto-applies high-confidence accept/decline decisions (anything else waits for admin review).
                  Off: replies go straight to the admin inbox and StackIQ does not read them.
                </Typography>
              </Box>
            }
          />
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={() => setEdit(undefined)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={submit}
            disabled={
              submitting ||
              (edit?.endpointType === 'email' && !edit?.email.trim()) ||
              (edit?.endpointType === 'webhook' && !edit?.webhookUrl.trim())
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EndpointConfigTable;
